import React, { useState } from "react";

import { uploadMenuItemMedia } from "services/uploads/rest";
import { MediaItemDto, MediaKind } from "services/menu/restModels";

import { useActiveLanguage } from "shared/hooks/useActiveLanguage";
import { useMenuSettings } from "shared/hooks/useMenuSettings";
import {
    localizedText,
    setLocalizedValue,
    trimLocalizedMap,
} from "shared/lib/localizedMap";
import { createDecimalInputHandler } from "shared/lib/decimalInput";
import {
    MENU_ITEM_MEDIA_ACCEPT,
    MENU_ITEM_MEDIA_HINT,
    UNSUPPORTED_MEDIA_ERROR,
    validateMenuItemMedia,
} from "shared/lib/mediaUpload";
import { MEASUREMENT_UNIT_OPTIONS } from "shared/lib/measurement";
import {
    Button,
    DeleteIcon,
    FieldGrid,
    IconButton,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    Select,
    Spinner,
    Stack,
    TextInput,
    Textarea,
    UploadDropzone,
} from "shared/ui";

import { useMenuEditorContext } from "features/MenuEditor/context";

import { LanguageRow, NutritionBlock, NutritionLabel, PhotoRow, Preview, VideoPreview } from "./styles";
import { parseDecimalInput, sanitizeDecimalInput } from "shared/lib/decimalInput";

export const CreateItemDialog = () => {
    const state = useMenuEditorContext();

    const [name, setName] = useState<Record<string, string>>({});
    const [description, setDescription] = useState<Record<string, string>>({});
    const [priceToShow, setPriceToShow] = useState<string>("");
    const [categoryId, setCategoryId] = useState<number>(
        state.activeCategory?.id ?? state.categories[0]?.id ?? 0
    );
    const [media, setMedia] = useState<MediaItemDto[]>([]);
    const [uploading, setUploading] = useState<boolean>(false);

    const [weight, setWeight] = useState<string>("");
    const [measurement, setMeasurement] = useState<string>("г");
    const [calories, setCalories] = useState<number | null>(null);
    const [caloriesToShow, setCaloriesToShow] = useState<string>("");
    const [proteins, setProteins] = useState<number | null>(null);
    const [proteinsToShow, setProteinsToShow] = useState<string>("");
    const [carbohydrates, setCarbohydrates] = useState<number | null>(null);
    const [carbohydratesToShow, setCarbohydratesToShow] = useState<string>("");
    const [fats, setFats] = useState<number | null>(null);
    const [fatsToShow, setFatsToShow] = useState<string>("");

    const setCaloriesHandler = createDecimalInputHandler(setCalories, setCaloriesToShow);
    const setProteinsHandler = createDecimalInputHandler(setProteins, setProteinsToShow);
    const setCarbohydratesHandler = createDecimalInputHandler(
        setCarbohydrates,
        setCarbohydratesToShow
    );
    const setFatsHandler = createDecimalInputHandler(setFats, setFatsToShow);

    const { settings } = useMenuSettings(state.menuId);
    const languages = settings?.availableLanguages.enabledLocales || [];
    const { activeLang, setActiveLang } = useActiveLanguage(state.locale, languages);

    const upload = async (files: File[]) => {
        // Пустой список — дропзона отсеяла файл по accept. Без своего сообщения перетаскивание
        // «неправильного» файла выглядит так, будто кабинет молча ничего не сделал.
        if (files.length === 0) {
            state.notify.showError(UNSUPPORTED_MEDIA_ERROR);
            return;
        }

        const { kind, error: validationError } = await validateMenuItemMedia(files[0]);

        if (kind === null || validationError !== null) {
            state.notify.showError(validationError);
            return;
        }

        setUploading(true);

        try {
            setMedia([await uploadMenuItemMedia(files[0])]);
        } catch (error) {
            state.notify.showError("Не удалось загрузить файл", error);
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        const price = state.currencyFormatter.toSend(
            parseDecimalInput(priceToShow) ?? 0
        );

        const created = await state.mutations.createItem({
            menuId: state.menuId,
            menuCategoryId: categoryId,
            name: trimLocalizedMap(name),
            description: trimLocalizedMap(description),
            sellPrice: { currencyId: state.currencyId, price },
            media,
            weight: {
                value: weight.trim() || null,
                measurement: weight.trim() ? measurement : null,
            },
            calories: { calories, proteins, carbohydrates, fats },
        });

        if (created) {
            state.closeDialog();
            state.selectCategory(categoryId);
        }
    };

    const canSubmit =
        Object.values(name).some((value) => value.trim().length > 0) &&
        categoryId > 0 &&
        !state.mutations.saving;

    return (
        <Modal
            open
            onClose={state.closeDialog}
            title="Новая позиция"
            actions={
                <>
                    <Button
                        variant="outlined"
                        color="neutral"
                        disabled={state.mutations.saving}
                        onClick={state.closeDialog}
                    >
                        Отмена
                    </Button>
                    <Button variant="contained" disabled={!canSubmit} onClick={submit}>
                        Создать
                    </Button>
                </>
            }
        >
            <Stack $gap={16}>
                {languages.length > 1 && (
                    <LanguageRow>
                        <LanguageSegmentedControl
                            languages={languages}
                            activeLang={activeLang}
                            primaryLang={state.locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !name[lang]}
                        />
                    </LanguageRow>
                )}

                <LabeledField label="Название">
                    <TextInput
                        value={name[activeLang]}
                        placeholder="Например, Капучино"
                        onChange={(value) =>
                            setName(setLocalizedValue(name, activeLang, value))
                        }
                    />
                </LabeledField>

                <LabeledField label="Описание" hint="Гость видит описание в карточке позиции">
                    <Textarea
                        value={description[activeLang]}
                        placeholder="Состав, вкус, особенности подачи"
                        rows={3}
                        onChange={(value) =>
                            setDescription(setLocalizedValue(description, activeLang, value))
                        }
                    />
                </LabeledField>

                <FieldGrid>
                    <LabeledField label="Цена">
                        <TextInput
                            value={priceToShow}
                            placeholder="0"
                            suffix={state.currencyShortName}
                            onChange={(value) =>
                                setPriceToShow(sanitizeDecimalInput(value))
                            }
                        />
                    </LabeledField>

                    <LabeledField label="Категория">
                        <Select
                            value={categoryId}
                            onChange={(value) => setCategoryId(Number(value))}
                            options={state.categories.map((category) => ({
                                value: category.id,
                                label: localizedText(category.name, state.locale),
                            }))}
                        />
                    </LabeledField>
                </FieldGrid>

                <LabeledField label="Медиа">
                    <PhotoRow>
                        {uploading && <Spinner size={24} />}

                        {!uploading && media.length > 0 && (
                            <>
                                {media[0].kind === MediaKind.video ? (
                                    <VideoPreview src={media[0].source} preload="metadata" muted />
                                ) : (
                                    <Preview
                                        src={media[0].card || media[0].source}
                                        alt=""
                                    />
                                )}
                                <IconButton
                                    label="Удалить файл"
                                    tone="danger"
                                    onClick={() => setMedia([])}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        )}

                        {!uploading && media.length === 0 && (
                            <UploadDropzone
                                onDrop={upload}
                                height={88}
                                accept={MENU_ITEM_MEDIA_ACCEPT}
                                hint={`${MENU_ITEM_MEDIA_HINT} · остальное добавите в редакторе позиции`}
                            />
                        )}
                    </PhotoRow>
                </LabeledField>

                <NutritionBlock>
                    <LabeledField label="Вес / объём">
                        <FieldGrid $min={120}>
                            <TextInput value={weight} onChange={setWeight} />
                            <Select
                                value={measurement}
                                onChange={setMeasurement}
                                options={MEASUREMENT_UNIT_OPTIONS}
                            />
                        </FieldGrid>
                    </LabeledField>

                    <NutritionLabel>на 100 г</NutritionLabel>

                    <FieldGrid $min={120}>
                        <LabeledField label="Килокалории">
                            <TextInput value={caloriesToShow} onChange={setCaloriesHandler} />
                        </LabeledField>
                        <LabeledField label="Белки">
                            <TextInput value={proteinsToShow} onChange={setProteinsHandler} />
                        </LabeledField>
                        <LabeledField label="Углеводы">
                            <TextInput
                                value={carbohydratesToShow}
                                onChange={setCarbohydratesHandler}
                            />
                        </LabeledField>
                        <LabeledField label="Жиры">
                            <TextInput value={fatsToShow} onChange={setFatsHandler} />
                        </LabeledField>
                    </FieldGrid>
                </NutritionBlock>
            </Stack>
        </Modal>
    );
};
